import React, { useState, useEffect, useCallback, useMemo, memo, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useSettings } from '../contexts/SettingsContext';
import { useDarkMode } from '../contexts/DarkModeContext';
import { ErrorBoundary } from 'react-error-boundary';
import MobileLayout from './dashboard/MobileLayout';
import DesktopLayout from './dashboard/DesktopLayout';
import LoadingAnimation from './LoadingAnimation';
import OnboardingCarousel from './onboarding/OnboardingCarousel';
import useOnboarding from '../hooks/useOnboarding';
import { getRandomGreeting } from '../data/greetings';
import { fetchDashboardData } from '../services/dashboardService';
import { fetchPrayerTimes } from '../services/prayerService';
import { useLocation as useRouterLocation } from 'react-router-dom';
import { useLocation } from '../contexts/LocationContext';
import { haveAllPrayersPassed } from '../utils/prayerTimeUtils';

const getDeviceType = () => {
  const width = window.innerWidth;
  if (width < 768) return 'mobile';
  if (width < 1024) return 'tablet';
  return 'desktop';
};

const ErrorFallback = ({ error }) => (
  <div className="text-center p-4">
    <h2 className="text-xl font-medium mb-2">Something went wrong</h2>
    <p className="text-sm text-gray-600">{error.message}</p>
  </div>
);

const MemoizedMobileLayout = memo(MobileLayout);
const MemoizedDesktopLayout = memo(DesktopLayout);
const MemoizedOnboardingCarousel = memo(OnboardingCarousel);

const Dashboard = () => {
  const { currentUser } = useAuth();
  const { settings, updateSettings, isInitialized } = useSettings();
  const { isDarkMode } = useDarkMode();
  const { location, error: locationError } = useLocation();
  const routerLocation = useRouterLocation();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState({
    dateTime: null,
    prayerTimes: null,
    duas: [],
    hadiths: [],
    adhkarCompleted: {},
    currentDuaIndex: 0,
    currentHadithIndex: 0
  });
  const { showOnboarding, completeOnboarding } = useOnboarding();
  const [greeting] = useState(getRandomGreeting());
  const [deviceType, setDeviceType] = useState(getDeviceType());
  const isLoadingRef = useRef(false);
  const dataTimestampRef = useRef(null);
  const containerRef = useRef(null);
  const lastPrayerTimesDateRef = useRef(null);
  const lastDuasDateRef = useRef(null);
  const lastHadithsDateRef = useRef(null);
  const hasLoadedBasicDataRef = useRef(false);

  // Reset scroll position to top when component mounts (mobile only)
  useEffect(() => {
    if (deviceType === 'mobile') {
      console.log('📱 Dashboard: Resetting scroll position to top');
      
      // Function to reset all scroll positions
      const resetAllScrollPositions = () => {
        // Reset window scroll
        window.scrollTo(0, 0);
        
        // Reset container scroll if ref is available
        if (containerRef.current) {
          containerRef.current.scrollTop = 0;
        }
        
        // Reset any scroll containers
        const scrollContainers = document.querySelectorAll('.overflow-auto, .overflow-y-auto, .overflow-scroll, .overflow-y-scroll');
        scrollContainers.forEach(container => {
          if (container.scrollTop > 0) {
            container.scrollTop = 0;
          }
        });
      };
      
      // Initial reset
      resetAllScrollPositions();
      
      // Set up a MutationObserver to detect when content is loaded and reset scroll again
      let observer;
      if (containerRef.current) {
        observer = new MutationObserver((mutations) => {
          // Check if mutations include content being added
          const hasContentChanges = mutations.some(mutation => 
            mutation.type === 'childList' && mutation.addedNodes.length > 0
          );
          
          if (hasContentChanges) {
            console.log('📱 Dashboard: Content changed, resetting scroll position');
            resetAllScrollPositions();
          }
        });
        
        observer.observe(containerRef.current, {
          childList: true,
          subtree: true
        });
      }
      
      // For some browsers/devices, we need a slight delay to ensure the scroll reset works
      const timeoutId = setTimeout(() => {
        resetAllScrollPositions();
      }, 100);
      
      // Also reset after images and other resources load
      const loadHandler = () => {
        resetAllScrollPositions();
      };
      
      window.addEventListener('load', loadHandler);
      
      return () => {
        clearTimeout(timeoutId);
        window.removeEventListener('load', loadHandler);
        if (observer) {
          observer.disconnect();
        }
      };
    }
  }, [deviceType]);

  const handleDashboardUpdate = useCallback((newData) => {
    console.log('[Dashboard] handleDashboardUpdate called with:', newData);
    
    // Check if we're updating currentDuaIndex or currentHadithIndex
    if (newData.currentDuaIndex !== undefined || newData.currentHadithIndex !== undefined) {
      console.log('[Dashboard] Updating navigation indices:', {
        currentDuaIndex: newData.currentDuaIndex,
        currentHadithIndex: newData.currentHadithIndex
      });
    }
    
    setDashboardData(prevData => {
      // Skip update if no actual changes
      let hasChanges = false;
      const updatedData = { ...prevData };
      
      for (const key in newData) {
        if (Object.prototype.hasOwnProperty.call(newData, key)) {
          if (newData[key] !== null && newData[key] !== undefined) {
            // Only update if the value has actually changed
            // For objects, compare stringified version
            if (typeof newData[key] === 'object') {
              if (JSON.stringify(updatedData[key]) !== JSON.stringify(newData[key])) {
                updatedData[key] = newData[key];
                hasChanges = true;
              }
            } else if (updatedData[key] !== newData[key]) {
              updatedData[key] = newData[key];
              hasChanges = true;
            }
          }
        }
      }
      
      // If valid prayerTimes are present, clear any stale error
      if (updatedData.prayerTimes && updatedData.prayerTimes.times && 
          Object.keys(updatedData.prayerTimes.times).length > 0) {
        updatedData.prayerTimesError = null;
      }
      
      // Only log if there were actual changes
      if (hasChanges) {
        console.log('[Dashboard] Updated dashboardData:', updatedData);
        return updatedData;
      }
      
      // Return previous state if no changes
      return prevData;
    });
    
    setLoading(false);
  }, []);

  const loadData = useCallback(async (force = false) => {
    // Skip if already loading and not forced
    if ((!force && isLoadingRef.current)) {
      return;
    }

    try {
      isLoadingRef.current = true;
      setLoading(true);
      const now = new Date();
      const todayStr = now.toISOString().split('T')[0];
      
      // If user is logged in but doesn't have location/coordinates, don't try to fetch prayer times
      if (currentUser && (!settings?.location || !settings?.coordinates)) {
        console.log('[Dashboard] Skipping prayer times fetch - missing location/coordinates');
        // Still load dateTime and other content
        try {
          const dateTime = await import('../services/dateTimeService').then(module => module.fetchDateTime());
          handleDashboardUpdate({
            dateTime,
            prayerTimesError: "Location not set"
          });
        } catch (error) {
          console.error("Error loading dateTime:", error);
        }
        // Proceed with fetching dashboard data without trying prayer times
        await fetchDashboardData(currentUser, settings, handleDashboardUpdate, dashboardData);
        dataTimestampRef.current = Date.now();
        isLoadingRef.current = false;
        return;
      }
      
      // Normal flow continues for users with location set or guests
      let shouldFetchPrayerTimes = true;
      let shouldFetchDuas = true;
      let shouldFetchHadiths = true;
      let shouldFetchDateTime = true;
      
      // Check if we already have valid cached date time data
      try {
        const storedDateTime = localStorage.getItem('dateTime');
        if (storedDateTime) {
          const { data, timestamp } = JSON.parse(storedDateTime);
          const storedDate = new Date(timestamp);
          // Use stored date time if it's from today
          if (storedDate.toDateString() === now.toDateString()) {
            shouldFetchDateTime = false;
            // If we already have date time data in state, no need to update
            if (!dashboardData.dateTime) {
              handleDashboardUpdate({ dateTime: data });
            }
          }
        }
      } catch (e) {
        console.warn('Error checking dateTime in localStorage:', e);
      }
      
      // Check if we already have valid prayerTimes for today and Isha has not passed
      if (dashboardData.prayerTimes && dashboardData.prayerTimes.times) {
        if (lastPrayerTimesDateRef.current === todayStr) {
          const allPassed = haveAllPrayersPassed(dashboardData.prayerTimes.times, now, settings?.tunedTimes || {});
          if (!allPassed) {
            shouldFetchPrayerTimes = false;
          }
        }
      }
      // Check if we already have valid duas/hadiths for today
      if (dashboardData.duas && dashboardData.duas.length > 0 && lastDuasDateRef.current === todayStr) {
        shouldFetchDuas = false;
      }
      if (dashboardData.hadiths && dashboardData.hadiths.length > 0 && lastHadithsDateRef.current === todayStr) {
        shouldFetchHadiths = false;
      }
      if (currentUser) {
        await fetchDashboardData(currentUser, settings, handleDashboardUpdate, dashboardData);
      } else if (settings && settings.location && settings.coordinates) {
        if (shouldFetchPrayerTimes) {
          try {
            const prayerTimes = await fetchPrayerTimes(settings);
            
            // Only fetch dateTime from the API if we need to
            let dateTime = dashboardData.dateTime;
            if (shouldFetchDateTime) {
              dateTime = await import('../services/dateTimeService').then(module => module.fetchDateTime());
            }
            
            handleDashboardUpdate({
              dateTime: dateTime,
              prayerTimes,
              prayerTimesError: null,
              location: settings.location,
              coordinates: settings.coordinates
            });
            lastPrayerTimesDateRef.current = todayStr;
          } catch (error) {
            console.error("Error loading prayer times for guest:", error);
            
            // Only fetch dateTime from the API if we need to
            let dateTime = dashboardData.dateTime;
            if (shouldFetchDateTime) {
              dateTime = await import('../services/dateTimeService').then(module => module.fetchDateTime());
            }
            
            handleDashboardUpdate({
              dateTime: dateTime,
              prayerTimes: null,
              prayerTimesError: "Failed to load prayer times",
              location: settings.location,
              coordinates: settings.coordinates
            });
          }
        }
        // Only fetch duas/hadiths if not already fetched today
        if (shouldFetchDuas || shouldFetchHadiths) {
          try {
            let duas = dashboardData.duas;
            let hadiths = dashboardData.hadiths;
            if (shouldFetchDuas) {
              duas = await import('../services/duaService').then(module => module.fetchDuas());
              lastDuasDateRef.current = todayStr;
            }
            if (shouldFetchHadiths) {
              hadiths = await import('../services/hadithService').then(module => module.fetchHadiths());
              lastHadithsDateRef.current = todayStr;
              console.log('[Dashboard] Loaded hadiths:', hadiths?.length);
            }
            
            // Only fetch dateTime from the API if we need to
            let dateTime = dashboardData.dateTime;
            if (shouldFetchDateTime) {
              dateTime = await import('../services/dateTimeService').then(module => module.fetchDateTime());
            }
            
            handleDashboardUpdate({
              dateTime: dateTime,
              duas: duas || [],
              currentDuaIndex: duas?.length ? Math.floor(Math.random() * duas.length) : 0,
              hadiths: hadiths || [],
              currentHadithIndex: hadiths?.length ? Math.floor(Math.random() * hadiths.length) : 0
            });
          } catch (error) {
            console.error("Error loading duas/hadiths for guest:", error);
          }
        }
      } else {
        // Guest user without location, just load basic data
        if (shouldFetchDateTime) {
          const dateTime = await import('../services/dateTimeService').then(module => module.fetchDateTime());
          handleDashboardUpdate({ 
            dateTime,
            prayerTimesError: "Location not set"
          });
        }
      }
      dataTimestampRef.current = Date.now();
    } catch (error) {
      console.error("Error loading dashboard data:", error);
    } finally {
      isLoadingRef.current = false;
      setLoading(false);
    }
  }, [
    currentUser,
    settings?.location,
    settings?.coordinates,
    settings?.calculationMethod,
    settings?.timezone,
    settings?.tunedTimes,
    handleDashboardUpdate,
    // Only reference specific properties of dashboardData that we actually use to determine whether to fetch
    dashboardData.dateTime,
    dashboardData.prayerTimes?.times,
    dashboardData.duas?.length,
    dashboardData.hadiths?.length,
    dashboardData.currentDuaIndex,
    dashboardData.currentHadithIndex
  ]);

  // Add a debounce utility to prevent multiple loadData calls
  const debounceRef = useRef(null);
  const debouncedLoadData = useCallback((force = false) => {
    // Clear any pending debounced calls
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    
    // Set up a new debounced call
    debounceRef.current = setTimeout(() => {
      console.log('[Dashboard] Debounced loadData running with force:', force);
      loadData(force);
      debounceRef.current = null;
    }, 200); // 200ms debounce time
  }, [loadData]);

  // Initial data load - Fix the condition to consider the case when location/coordinates might be missing
  useEffect(() => {
    // Skip if not initialized yet
    if (!isInitialized) {
      return;
    }
    
    // Skip this effect if we're still loading or already have data
    if (isLoadingRef.current) {
      console.log('[Dashboard] Skipping initial load - already loading');
      return;
    }
    
    console.log('[Dashboard] Initial load check', {
      hasUser: !!currentUser,
      hasLocation: !!settings?.location,
      hasCoordinates: !!settings?.coordinates,
      isInitialized,
      hasData: !!dashboardData.dateTime
    });
    
    if (currentUser || (settings && settings.location && settings.coordinates)) {
      debouncedLoadData(true);
    } else if (!hasLoadedBasicDataRef.current) {
      // For guests without location, or users without location, only load basic data once
      console.log('[Dashboard] Loading basic data for user without location');
      const loadBasicDataOnly = async () => {
        try {
          isLoadingRef.current = true;
          setLoading(true);
          const dateTime = await import('../services/dateTimeService').then(module => module.fetchDateTime());
          const duas = await import('../services/duaService').then(module => module.fetchDuas());
          const hadiths = await import('../services/hadithService').then(module => module.fetchHadiths());
          
          handleDashboardUpdate({
            dateTime,
            duas: duas || [],
            currentDuaIndex: duas?.length ? Math.floor(Math.random() * duas.length) : 0,
            hadiths: hadiths || [],
            currentHadithIndex: hadiths?.length ? Math.floor(Math.random() * hadiths.length) : 0,
            prayerTimesError: "Location not set"
          });
          
          hasLoadedBasicDataRef.current = true;
        } catch (error) {
          console.error("Error loading basic data:", error);
        } finally {
          isLoadingRef.current = false;
          setLoading(false);
        }
      };
      loadBasicDataOnly();
    }
  }, [
    currentUser, 
    isInitialized,
    debouncedLoadData,
    // Only depend on existence of location/coordinates, not their values
    !!settings?.location && !!settings?.coordinates,
    handleDashboardUpdate
  ]);

  // Ensure loadData is called whenever settings.location or settings.coordinates become available (hydration)
  useEffect(() => {
    // Skip if not initialized or still loading
    if (!isInitialized || isLoadingRef.current) {
      return;
    }
    
    // Only trigger when a user goes from no location to having location
    const hasLocation = settings?.location && settings?.coordinates;
    const hasPrayerTimesError = dashboardData.prayerTimesError === "Location not set";
    const needsPrayerTimes = !dashboardData.prayerTimes || !dashboardData.prayerTimes.times;
    
    if (hasLocation && (hasPrayerTimesError || needsPrayerTimes)) {
      console.log('[Dashboard] Location/coordinates became available, loading prayer times');
      debouncedLoadData(true);
    }
  }, [
    isInitialized, 
    debouncedLoadData, 
    !!settings?.location && !!settings?.coordinates,
    dashboardData.prayerTimesError
  ]);

  // Reset session data ref when user changes
  useEffect(() => {
    // Reset the session ref if the user changes (e.g., login/logout)
    hasLoadedBasicDataRef.current = false;
  }, [currentUser]);
  
  // Refresh data periodically (every 5 minutes)
  useEffect(() => {
    const interval = setInterval(() => {
      // Only refresh for users with location set
      if (isInitialized && !isLoadingRef.current && (
        (currentUser && settings?.location && settings?.coordinates) || 
        (!currentUser && settings?.location && settings?.coordinates)
      )) {
        console.log('[Dashboard] Periodic refresh triggered');
        loadData(true);
      }
    }, 5 * 60 * 1000); // 5 minutes

    return () => clearInterval(interval);
  }, [currentUser, isInitialized, loadData, !!settings?.location && !!settings?.coordinates]);

  // Refresh data when tab becomes visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && !isLoadingRef.current) {
        // Only reload if data is older than 1 minute and location is set
        const dataAge = Date.now() - (dataTimestampRef.current || 0);
        if (dataAge > 60 * 1000 && settings?.location && settings?.coordinates) {
          console.log('[Dashboard] Visibility changed, refreshing data');
          loadData(true);
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [loadData, !!settings?.location && !!settings?.coordinates]);

  useEffect(() => {
    return () => {
      isLoadingRef.current = false;
    };
  }, []);

  const handleResize = useCallback(() => {
    setDeviceType(getDeviceType());
  }, []);

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [handleResize]);

  const layoutProps = useMemo(() => ({
    currentUser,
    settings,
    isDarkMode,
    dashboardData,
    greeting,
    setDashboardData: handleDashboardUpdate,
    isLoading: loading,
    isInitialized,
    location,
    locationError
  }), [currentUser, settings, isDarkMode, dashboardData, greeting, handleDashboardUpdate, loading, isInitialized, location, locationError]);

  // Debug settings values related to location
  useEffect(() => {
    console.log('[Dashboard] Settings location state:', {
      hasLocation: settings ? !!settings.location : false, 
      hasCoordinates: settings ? !!settings.coordinates : false,
      locationValue: settings?.location,
      coordinatesValue: settings?.coordinates,
      isLoading: loading || !isInitialized
    });
  }, [settings, loading, isInitialized]);

  // Check if we're on the main dashboard page and not on mobile
  const isDesktopDashboard = routerLocation.pathname === '/' && deviceType !== 'mobile';
  
  // Always render the layout with skeletons when loading
  return (
    <>
      <div
        ref={containerRef}
        className={`min-h-screen transition-colors duration-500 ${
          isDesktopDashboard ?
            (isDarkMode ? 'bg-[#111111]' : 'bg-white dark:bg-[#111111]')
            : 'bg-white'
        }`}
      >
        {deviceType === 'mobile' ? (
          <ErrorBoundary FallbackComponent={ErrorFallback}>
            <MemoizedMobileLayout {...layoutProps} />
          </ErrorBoundary>
        ) : (
          <ErrorBoundary FallbackComponent={ErrorFallback}>
            <MemoizedDesktopLayout {...layoutProps} />
          </ErrorBoundary>
        )}
      </div>
      
      {/* Onboarding carousel (step-by-step setup) */}
      {showOnboarding && (
        <MemoizedOnboardingCarousel
          userId={currentUser ? currentUser.uid : undefined}
          onComplete={completeOnboarding}
        />
      )}
    </>
  );
};

export default Dashboard;
